import { useState, useEffect } from 'react';
import { getEmployees, createEmployee, updateEmployee, deleteEmployee, generateEmployeeTrackingToken, formatIQD, formatUSD } from '../utils/api';
import toast from 'react-hot-toast';
import { Users, Plus, X, Trash2, Edit, Calendar, UserCheck, Briefcase, DollarSign, CheckCircle, Link, ChevronDown, ChevronRight, Shield } from 'lucide-react';

export default function EmployeesPage() {
    const PERMISSIONS_LIST = [
        { id: '/', label: 'الرئيسية' },
        { id: '/tasks', label: 'لوحة المهام' },
        { id: '/employees', label: 'الموظفين' },
        { id: '/team', label: 'أداء الفريق (داخلي)' },
        { id: '/clients', label: 'العملاء' },
        { id: '/projects', label: 'المشاريع' },
        { id: '/subscriptions', label: 'الاشتراكات' },
        { id: '/deliverables', label: 'التسليمات' },
        { id: '/packages', label: 'إدارة الباقات' },
        { id: '/invoices', label: 'الفواتير' },
        { id: '/expenses', label: 'المصاريف' },
        { id: '/reports', label: 'التقارير' },
        { id: '/commissions', label: 'العمولات' },
        { id: '/settings', label: 'الإعدادات' },
    ];

    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [showInactive, setShowInactive] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, item: null, title: '', message: '' });
    const [form, setForm] = useState({
        name: '', role: '', base_salary_usd: '', base_salary_iqd: '', tasks: '', join_date: new Date().toISOString().split('T')[0], active: true, commission_rate: '', baseline_seconds: 5000, track_seconds: true, bonus_rate: 250,
        track_target: false, baseline_target: 5000, bonus_rate_usd: 0, bonus_rate_iqd: 250, deduction_rate_usd: 0, deduction_rate_iqd: 250, username: '', password: '', permissions: ['/tasks']
    });

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await getEmployees();
            setEmployees(data);
        } catch {
            toast.error('❌ خطأ في تحميل بيانات الموظفين');
        }
        setLoading(false);
    };

    useEffect(() => { loadData(); }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingEmployee) {
                await updateEmployee(editingEmployee.id, form);
                toast.success('تم تحديث بيانات الموظف بنجاح');
            } else {
                await createEmployee(form);
                toast.success('تم إضافة الموظف بنجاح');
            }
            closeModal();
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.error || 'حدث خطأ في الحفظ');
        }
    };

    const handleDeleteConfirm = (emp) => {
        setConfirmDialog({
            isOpen: true,
            item: emp,
            title: 'تأكيد عملية الحذف',
            message: `هل أنت متأكد من رغبتك في حذف الموظف "${emp.name}" نهائياً؟`
        });
    };

    const executeDelete = async () => {
        const emp = confirmDialog.item;
        if (!emp) return;
        try {
            await deleteEmployee(emp.id);
            toast.success('تم الحذف بنجاح');
            loadData();
        } catch (err) {
            if (err.response?.data?.softDeleted) {
                toast.success(err.response.data.error, { icon: '⚠️', duration: 6000 });
                loadData();
            } else {
                toast.error(err.response?.data?.error || 'لا يمكن حذف موظف مرتبط بمصروفات أو نظام الدخول');
            }
        } finally {
            setConfirmDialog({ isOpen: false, item: null, message: '', title: '' });
        }
    };

    const openEdit = (emp) => {
        setEditingEmployee(emp);
        setForm({
            name: emp.name,
            role: emp.role,
            base_salary_usd: emp.base_salary_usd || '',
            base_salary_iqd: emp.base_salary_iqd || '',
            tasks: emp.tasks || '',
            join_date: emp.join_date || new Date().toISOString().split('T')[0],
            active: emp.active === 1,
            commission_rate: emp.commission_rate || '',
            baseline_seconds: emp.baseline_seconds !== null ? emp.baseline_seconds : 5000,
            track_seconds: emp.track_seconds !== null ? emp.track_seconds === 1 : true,
            bonus_rate: emp.bonus_rate !== null ? emp.bonus_rate : 250,
            track_target: false,
            baseline_target: emp.baseline_target || 0,
            bonus_rate_usd: emp.bonus_rate_usd || 0,
            bonus_rate_iqd: emp.bonus_rate_iqd || 0,
            deduction_rate_usd: emp.deduction_rate_usd || 0,
            deduction_rate_iqd: emp.deduction_rate_iqd || 0,
            username: emp.username || '',
            password: '',
            permissions: typeof emp.permissions === 'string' ? JSON.parse(emp.permissions) : (emp.permissions || ['/tasks'])
        });
        setShowAdd(true);
    };

    const closeModal = () => {
        setShowAdd(false);
        setEditingEmployee(null);
        setForm({ name: '', role: '', base_salary_usd: '', base_salary_iqd: '', tasks: '', join_date: new Date().toISOString().split('T')[0], active: true, commission_rate: '', baseline_seconds: 5000, track_seconds: true, bonus_rate: 250, track_target: false, baseline_target: 5000, bonus_rate_usd: 0, bonus_rate_iqd: 250, deduction_rate_usd: 0, deduction_rate_iqd: 250, username: '', password: '', permissions: ['/tasks'] });
    };

    const handleCopyLink = async (emp) => {
        try {
            const res = await generateEmployeeTrackingToken(emp.id);
            const link = `${window.location.origin}/employee-track/${res.tracking_token}`;
            await navigator.clipboard.writeText(link);
            toast.success('تم نسخ رابط التتبع الخاص بالموظف وتفعيل الميزة');
        } catch {
            toast.error('فشل في توليد رابط التتبع');
        }
    };

    const displayEmployees = employees.filter(e => {
        if (e.id === 0 || e.name === 'System (Internal)') return false;
        return showInactive ? true : e.active === 1 || e.active === true;
    });
    const totalSalariesUSD = displayEmployees.reduce((sum, e) => sum + (e.active ? (e.base_salary_usd || 0) : 0), 0);
    const totalSalariesIQD = displayEmployees.reduce((sum, e) => sum + (e.active ? (e.base_salary_iqd || 0) : 0), 0);
    const activeCount = employees.filter(e => e.id !== 0 && e.name !== 'System (Internal)' && e.active).length;

    // Group employees by team
    const [expandedTeams, setExpandedTeams] = useState({});
    const toggleTeam = (teamKey) => setExpandedTeams(prev => ({ ...prev, [teamKey]: !prev[teamKey] }));

    const grouped = displayEmployees.reduce((acc, emp) => {
        const key = emp.team_name || '__none__';
        if (!acc[key]) acc[key] = { team_name: emp.team_name, team_id: emp.team_id, members: [] };
        acc[key].members.push(emp);
        return acc;
    }, {});
    // Sort: teams first, then ungrouped
    const teamGroups = Object.entries(grouped)
        .filter(([k]) => k !== '__none__')
        .sort((a, b) => a[0].localeCompare(b[0], 'ar'));
    const ungrouped = grouped['__none__']?.members || [];

    const inputClasses = "w-full px-4 py-2.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl text-sm outline-none transition-all focus:border-primary focus:bg-white dark:bg-dark-deeper dark:focus:bg-dark-deeper focus:ring-4 focus:ring-primary/10 placeholder:text-gray-400";
    const labelClasses = "block text-xs font-bold text-gray-600 dark:text-gray-300 mb-1.5";

    const renderRow = (emp) => (
        <tr key={emp.id} className="hover:bg-gray-50 dark:bg-white/5 dark:hover:bg-white/5/50 transition-colors group">
            <td className="px-5 py-4">
                <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg ${emp.active ? 'bg-primary/10 text-primary' : 'bg-gray-100 dark:bg-white/10 text-gray-400'}`}>
                        {emp.name.charAt(0)}
                    </div>
                    <div>
                        <p className="font-bold text-dark dark:text-gray-100 flex items-center gap-2">
                            {emp.name}
                            {emp.is_team_leader ? (
                                <span className="text-[10px] bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400 px-1.5 py-0.5 rounded-md font-bold">قائد</span>
                            ) : null}
                            {emp.active ? (
                                <CheckCircle size={14} className="text-success" />
                            ) : (
                                <span className="text-[10px] bg-danger/10 text-danger px-1.5 py-0.5 rounded-md">موقوف</span>
                            )}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-0.5"><Briefcase size={12} /> {emp.role}</p>
                    </div>
                </div>
            </td>
            <td className="px-5 py-4 text-sm text-gray-600 dark:text-gray-300">
                <p className="line-clamp-2">{emp.tasks || '—'}</p>
            </td>
            <td className="px-5 py-4 text-sm font-medium text-gray-600 dark:text-gray-300">
                <div className="flex items-center gap-1.5"><Calendar size={14} className="text-gray-400" /> {emp.join_date || '—'}</div>
            </td>
            <td className="px-5 py-4">
                <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-1.5">
                        <span className="text-sm font-black text-dark dark:text-gray-100">{formatUSD(emp.base_salary_usd || 0)}</span>
                        {emp.commission_rate > 0 && (
                            <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded font-bold">%{emp.commission_rate} عمولة</span>
                        )}
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{formatIQD(emp.base_salary_iqd || 0)}</span>
                </div>
            </td>
            <td className="px-5 py-4 text-center">
                <div className="flex justify-center items-center gap-2 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                    <button onClick={() => handleCopyLink(emp)} title="نسخ رابط التتبع" className="p-2 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded-lg transition-colors"><Link size={16} /></button>
                    <button onClick={() => openEdit(emp)} className="p-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"><Edit size={16} /></button>
                    <button onClick={() => handleDeleteConfirm(emp)} className="p-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors"><Trash2 size={16} /></button>
                </div>
            </td>
        </tr>
    );

    return (
        <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
            {/* Confirm Modal */}
            {confirmDialog.isOpen && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(15, 30, 51, 0.65)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 10000, backdropFilter: 'blur(4px)'
                }}>
                    <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{
                        background: 'var(--bg-flat-white, #fff)', borderRadius: 16, width: 400, maxWidth: '90%', padding: '32px 24px',
                        fontFamily: 'Tajawal, sans-serif', direction: 'rtl', textAlign: 'center',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
                    }}>
                        <div style={{ fontSize: 44, marginBottom: 12 }}>⚠️</div>
                        <h3 style={{ margin: '0 0 10px', color: 'var(--text-dark-h, #0F1E33)', fontSize: 18, fontWeight: 700 }}>
                            {confirmDialog.title}
                        </h3>
                        <p className="dark:!text-gray-400" style={{ margin: '0 0 24px', color: 'var(--text-gray, #64748B)', fontSize: 14, lineHeight: 1.6 }}>
                            {confirmDialog.message}
                        </p>
                        <div style={{ display: 'flex', gap: 12, justifyItems: 'center', justifyContent: 'center' }}>
                            <button
                                onClick={() => setConfirmDialog({ isOpen: false, item: null, message: '', title: '' })}
                                style={{ padding: '8px 18px', background: '#F1F5F9', color: 'var(--text-gray, #64748B)', border: 'none', borderRadius: 8, cursor: 'pointer', fontFamily: 'Tajawal, sans-serif', fontSize: 13, fontWeight: 600 }}
                            >
                                إلغاء والتراجع
                            </button>
                            <button
                                onClick={executeDelete}
                                style={{ padding: '8px 18px', background: '#E74C3C', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontFamily: 'Tajawal, sans-serif', fontSize: 13, fontWeight: 600 }}
                            >
                                تأكيد الحذف
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-black text-dark dark:text-gray-100 flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-xl text-blue-600">
                            <Users size={24} />
                        </div>
                        إدارة الموظفين
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">تتبع فريق العمل، المهام اليومية، ورواتب الموظفين.</p>
                </div>
                <button
                    onClick={() => setShowAdd(true)}
                    className="flex items-center justify-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl text-sm font-bold shadow-lg shadow-primary/30 hover:bg-primary/90 hover:-translate-y-0.5 transition-all duration-200"
                >
                    <Plus size={18} /> إضافة موظف
                </button>
            </div>
 
            {/* Filter Toggle */}
            <div className="flex justify-start items-center p-4 bg-white dark:bg-dark-deeper rounded-2xl border border-gray-100 dark:border-white/5 shadow-sm">
                <label className="flex items-center gap-2 cursor-pointer select-none text-sm font-bold text-gray-600 dark:text-gray-300">
                    <input 
                        type="checkbox" 
                        checked={showInactive} 
                        onChange={e => setShowInactive(e.target.checked)} 
                        className="rounded border-gray-300 text-primary focus:ring-primary w-4.5 h-4.5 cursor-pointer accent-primary" 
                    />
                    <span>عرض الحسابات الموقوفة والموظفين السابقين 📦</span>
                </label>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white dark:bg-dark-deeper p-5 rounded-2xl border border-gray-100 dark:border-white/5 flex items-center justify-between shadow-sm hover:shadow-md transition-shadow">
                    <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 font-bold mb-1">العدد الإجمالي</p>
                        <h3 className="text-2xl font-black text-dark dark:text-gray-100">{displayEmployees.length}</h3>
                        <span className="text-xs text-success font-bold mt-1 block">{activeCount} موظفين نشطين</span>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-500 flex items-center justify-center"><UserCheck size={24} /></div>
                </div>
                <div className="bg-white dark:bg-dark-deeper p-5 rounded-2xl border border-gray-100 dark:border-white/5 flex items-center justify-between shadow-sm hover:shadow-md transition-shadow">
                    <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 font-bold mb-1">مجموع الرواتب ($)</p>
                        <h3 className="text-2xl font-black text-dark dark:text-gray-100">{formatUSD(totalSalariesUSD)}</h3>
                        <span className="text-xs text-gray-400 font-medium mt-1 block">للموظفين النشطين فقط</span>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-500 flex items-center justify-center"><DollarSign size={24} /></div>
                </div>
                <div className="bg-white dark:bg-dark-deeper p-5 rounded-2xl border border-gray-100 dark:border-white/5 flex items-center justify-between shadow-sm hover:shadow-md transition-shadow">
                    <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 font-bold mb-1">مجموع الرواتب (عراقي)</p>
                        <h3 className="text-2xl font-black text-dark dark:text-gray-100">{formatIQD(totalSalariesIQD)}</h3>
                        <span className="text-xs text-gray-400 font-medium mt-1 block">للموظفين النشطين فقط</span>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-green-50 text-green-500 flex items-center justify-center"><DollarSign size={24} /></div>
                </div>
            </div>

            {/* List */}
            <div className="bg-white dark:bg-dark-deeper rounded-2xl border border-gray-100 dark:border-white/5 shadow-sm overflow-hidden">
                {loading ? (
                    <div className="flex justify-center items-center h-40">
                        <div className="w-8 h-8 border-4 border-gray-200 dark:border-white/10 border-t-primary rounded-full animate-spin"></div>
                    </div>
                ) : displayEmployees.length === 0 ? (
                    <div className="p-16 text-center">
                        <div className="w-16 h-16 bg-gray-50 dark:bg-white/5 rounded-2xl border border-dashed border-gray-200 dark:border-white/10 flex items-center justify-center mx-auto mb-4 text-gray-400">
                            <Users size={28} />
                        </div>
                        <h3 className="text-lg font-bold text-dark dark:text-gray-100 mb-1">لم تقم بإضافة موظفين يدوياً</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">قم بالضغط على الأيقونة للبدء وتشكيل فريق عملك.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto custom-scrollbar">
                        <table className="w-full text-right">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-white/5 border-b border-gray-100 dark:border-white/5 text-gray-500 dark:text-gray-400">
                                    <th className="px-5 py-4 text-xs font-bold w-1/4">الموظف / الدور</th>
                                    <th className="px-5 py-4 text-xs font-bold w-1/4">المهام / الوصف</th>
                                    <th className="px-5 py-4 text-xs font-bold w-1/5">تاريخ الانضمام</th>
                                    <th className="px-5 py-4 text-xs font-bold w-1/5">الراتب / العمولة</th>
                                    <th className="px-5 py-4 text-xs font-bold w-32 text-center">إجراءات</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {/* Team groups */}
                                {teamGroups.map(([teamName, group]) => {
                                    const isOpen = expandedTeams[teamName] !== false; // default open
                                    return [
                                        <tr key={`team-${teamName}`}
                                            className="cursor-pointer hover:bg-indigo-50/50 dark:hover:bg-indigo-900/10 transition-colors"
                                            onClick={() => toggleTeam(teamName)}
                                        >
                                            <td colSpan={5} className="px-5 py-3">
                                                <div className="flex items-center gap-2">
                                                    {isOpen ? <ChevronDown size={16} className="text-indigo-500" /> : <ChevronRight size={16} className="text-gray-400" />}
                                                    <Shield size={15} className="text-indigo-500" />
                                                    <span className="font-black text-sm text-indigo-600 dark:text-indigo-400">{teamName}</span>
                                                    <span className="text-[11px] bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 px-2 py-0.5 rounded-full font-bold">
                                                        {group.members.length} أعضاء
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>,
                                        ...(isOpen ? group.members.map(emp => renderRow(emp)) : [])
                                    ];
                                })}
                                {/* Ungrouped employees */}
                                {ungrouped.length > 0 && teamGroups.length > 0 && (
                                    <tr>
                                        <td colSpan={5} className="px-5 py-3">
                                            <div className="flex items-center gap-2">
                                                <Users size={15} className="text-gray-400" />
                                                <span className="font-bold text-sm text-gray-500 dark:text-gray-400">بدون فريق</span>
                                                <span className="text-[11px] bg-gray-100 dark:bg-gray-800 text-gray-500 px-2 py-0.5 rounded-full font-bold">
                                                    {ungrouped.length}
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                {ungrouped.map(emp => renderRow(emp))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Add / Edit Modal */}
            {showAdd && (
                <div className="fixed inset-0 bg-dark/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in" onMouseDown={e => { if (e.target === e.currentTarget) closeModal(); }}>
                    <div className="bg-white dark:bg-dark-deeper rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col animate-scale-up">
                        <div className="p-5 border-b border-gray-100 dark:border-white/5 flex justify-between items-center bg-gray-50 dark:bg-white/5/50">
                            <h3 className="text-lg font-black text-dark dark:text-gray-100 flex items-center gap-2">
                                {editingEmployee ? <Edit size={20} className="text-primary" /> : <Users size={20} className="text-primary" />}
                                {editingEmployee ? 'تعديل بيانات الموظف' : 'موظف جديد'}
                            </h3>
                            <button onClick={closeModal} className="p-2 bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 hover:bg-gray-200 rounded-lg transition-colors"><X size={18} /></button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[75vh] custom-scrollbar flex flex-col gap-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClasses}>الاسم الكامل *</label>
                                    <input type='text' value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className={inputClasses} placeholder="مثال: أحمد محمد" />
                                </div>
                                <div>
                                    <label className={labelClasses}>المسمى الوظيفي (Role) *</label>
                                    <input type='text' value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} required className={inputClasses} placeholder="مثال: مصمم جرافيك" />
                                </div>
                            </div>

                            <div>
                                <label className={labelClasses}>المهام والمسؤوليات</label>
                                <textarea value={form.tasks} onChange={e => setForm({ ...form, tasks: e.target.value })} className={`${inputClasses} min-h-[80px]`} placeholder="أمثلة: متابعة حسابات السوشيال الميديا، الرد على العملاء..." />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClasses}>الراتب الأساسي ($)</label>
                                    <input type='number' value={form.base_salary_usd} onChange={e => setForm({ ...form, base_salary_usd: parseFloat(e.target.value) || '' })} className={inputClasses} />
                                </div>
                                <div>
                                    <label className={labelClasses}>الراتب (د.ع)</label>
                                    <input type='number' value={form.base_salary_iqd} onChange={e => setForm({ ...form, base_salary_iqd: parseFloat(e.target.value) || '' })} className={inputClasses} />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClasses}>تاريخ البدء</label>
                                    <input type='date' value={form.join_date} onChange={e => setForm({ ...form, join_date: e.target.value })} required className={inputClasses} />
                                </div>
                                <div>
                                    <label className={labelClasses}>نسبة العمولة الافتراضية (%)</label>
                                    <input type='number' step="0.1" value={form.commission_rate} onChange={e => setForm({ ...form, commission_rate: e.target.value })} className={inputClasses} placeholder="مثال: 10" />
                                </div>
                            </div>
                            <div className="mt-1 p-4 border border-blue-100 bg-blue-50/50 rounded-xl space-y-4">
                                <label className="flex items-center gap-3 cursor-pointer group">
                                    <div className="relative flex items-center justify-center">
                                        <input type="checkbox" checked={form.track_seconds} onChange={e => setForm({ ...form, track_seconds: e.target.checked })} className="peer sr-only" />
                                        <div className="w-5 h-5 bg-white dark:bg-dark-deeper border-2 border-primary/40 rounded peer-checked:bg-primary peer-checked:border-primary transition-colors"></div>
                                        <svg className="absolute w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                    </div>
                                    <span className="text-sm font-bold text-primary">تتبع دقائق الإنتاج وتسليمات الفيديوهات</span>
                                </label>
                                {form.track_seconds && (
                                    <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-blue-100/50">
                                        <div>
                                            <label className={labelClasses}>الوقت المعياري المطلوب (ثواني)</label>
                                            <input type='number' value={form.baseline_seconds} onChange={e => setForm({ ...form, baseline_seconds: parseInt(e.target.value) || 0 })} className={inputClasses} placeholder="مثال: 5000" />
                                        </div>
                                        <div>
                                            <label className={labelClasses}>سعر التكلفة لكل ثانية (د.ع)</label>
                                            <input type='number' value={form.bonus_rate} onChange={e => setForm({ ...form, bonus_rate: parseFloat(e.target.value) || 0 })} className={inputClasses} placeholder="مثال: 250" />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="mt-1 p-4 border border-purple-100 bg-purple-50/50 rounded-xl space-y-4">
                                <label className="flex items-center gap-3 cursor-pointer group">
                                    <div className="relative flex items-center justify-center">
                                        <input type="checkbox" checked={form.track_target} onChange={e => setForm({ ...form, track_target: e.target.checked })} className="peer sr-only" />
                                        <div className="w-5 h-5 bg-white dark:bg-dark-deeper border-2 border-purple-400 rounded peer-checked:bg-purple-600 peer-checked:border-purple-600 transition-colors"></div>
                                        <svg className="absolute w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                    </div>
                                    <span className="text-sm font-bold text-purple-700">تتبع أهداف الإنجاز (التارجت) للمهام العادية</span>
                                </label>
                                {form.track_target && (
                                    <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-purple-100/50">
                                        <div>
                                            <label className={labelClasses}>التارجت الشهري المطلوب</label>
                                            <input type='number' value={form.baseline_target} onChange={e => setForm({ ...form, baseline_target: parseInt(e.target.value) || 0 })} className={inputClasses} placeholder="مثال: 50 هدف" />
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className={labelClasses}>مكافأة الهدف ($)</label>
                                                <input type='number' value={form.bonus_rate_usd} onChange={e => setForm({ ...form, bonus_rate_usd: parseFloat(e.target.value) || 0 })} className={inputClasses} placeholder="1" />
                                            </div>
                                            <div>
                                                <label className={labelClasses}>مكافأة الهدف (د.ع)</label>
                                                <input type='number' value={form.bonus_rate_iqd} onChange={e => setForm({ ...form, bonus_rate_iqd: parseFloat(e.target.value) || 0 })} className={inputClasses} placeholder="1500" />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 col-span-2 mt-2">
                                            <div>
                                                <label className={labelClasses}>خصم النقص ($/مهمة)</label>
                                                <input type='number' value={form.deduction_rate_usd} onChange={e => setForm({ ...form, deduction_rate_usd: parseFloat(e.target.value) || 0 })} className={inputClasses} placeholder="1" />
                                            </div>
                                            <div>
                                                <label className={labelClasses}>خصم النقص (د.ع/مهمة)</label>
                                                <input type='number' value={form.deduction_rate_iqd} onChange={e => setForm({ ...form, deduction_rate_iqd: parseFloat(e.target.value) || 0 })} className={inputClasses} placeholder="1500" />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-white/10 space-y-4">
                                <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300">حساب الدخول (اختياري)</h3>
                                <p className="text-xs text-gray-500">قم بإعداد بيانات الدخول ليتمكن الموظف من رؤية لوحة المهام الخاصة به.</p>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelClasses}>اسم المستخدم</label>
                                        <input type='text' value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} className={inputClasses} placeholder="مثال: ahmed_video" autoComplete="off" />
                                    </div>
                                    <div>
                                        <label className={labelClasses}>كلمة المرور</label>
                                        <input type='password' value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className={inputClasses} placeholder={editingEmployee ? "اتركها فارغة لعدم التغيير" : "كلمة المرور"} autoComplete="new-password" />
                                    </div>
                                </div>
                                <div className="mt-4 border border-gray-100 dark:border-white/10 rounded-xl p-4 bg-white dark:bg-dark-deeper">
                                    <h4 className="text-xs font-bold text-gray-700 dark:text-gray-300 mb-2">صلاحيات الموظف (الصفحات التي تظهر في القائمة)</h4>
                                    <p className="text-[10px] text-gray-500 mb-3 block">إذا لم تقم بتحديد أي قائمة، فلن يتمكن الموظف من رؤيتها في حساب الدخول الخاص به.</p>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                        {PERMISSIONS_LIST.map(perm => {
                                            const isChecked = form.permissions.includes(perm.id);
                                            return (
                                                <label key={perm.id} className="flex items-center gap-2 cursor-pointer group hover:bg-gray-50 dark:hover:bg-white/5 p-1.5 rounded-lg transition-colors">
                                                    <div className="relative flex items-center justify-center shrink-0">
                                                        <input 
                                                            type="checkbox" 
                                                            checked={isChecked}
                                                            onChange={(e) => {
                                                                if (e.target.checked) setForm({...form, permissions: [...form.permissions, perm.id]});
                                                                else setForm({...form, permissions: form.permissions.filter(p => p !== perm.id)});
                                                            }}
                                                            className="peer sr-only" 
                                                        />
                                                        <div className="w-4 h-4 bg-white dark:bg-dark-deeper border-2 border-gray-300 dark:border-gray-500 rounded peer-checked:bg-primary peer-checked:border-primary transition-colors"></div>
                                                        <svg className="absolute w-3 h-3 text-white opacity-0 peer-checked:opacity-100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                                    </div>
                                                    <span className={`text-xs font-bold transition-colors ${isChecked ? 'text-primary dark:text-accent' : 'text-gray-600 dark:text-gray-400'}`}>
                                                        {perm.label}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {editingEmployee && (
                                <div className="mt-2 bg-gray-50 dark:bg-white/5 p-4 border border-gray-100 dark:border-white/5 rounded-xl">
                                    <label className="flex items-center gap-3 cursor-pointer group">
                                        <div className="relative flex items-center justify-center">
                                            <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} className="peer sr-only" />
                                            <div className="w-5 h-5 bg-white dark:bg-dark-deeper border-2 border-gray-300 rounded peer-checked:bg-success peer-checked:border-success transition-colors"></div>
                                            <svg className="absolute w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                        </div>
                                        <span className="text-sm font-bold text-gray-700 dark:text-gray-200">موظف نشط (على رأس العمل)</span>
                                    </label>
                                </div>
                            )}

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-white/5 mt-2">
                                <button type="button" onClick={closeModal} className="px-5 py-2.5 bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 font-bold text-sm rounded-xl hover:bg-gray-200 transition-colors">إلغاء</button>
                                <button type="submit" className="px-6 py-2.5 bg-primary text-white font-bold text-sm rounded-xl hover:bg-primary/90 shadow-md shadow-primary/20 transition-all">حفظ البيانات</button>
                            </div>
                        </form>
                    </div>
                </div >
            )
            }
        </div >
    );
}
